import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Framework from "@/pages/Framework";
import Listings from "@/pages/Listings";
import Results from "@/pages/Results";
import Testimonials from "@/pages/Testimonials";
import Awards from "@/pages/Awards";
import Calculators from "@/pages/Calculators";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import ListingDetail from "@/pages/ListingDetail";
import CaseStudyDetail from "@/pages/CaseStudyDetail";
import Contact from "@/pages/Contact";
import SiteTracking from "@/components/SiteTracking";
import { AuthProvider } from "@/admin/auth/AuthProvider";

// Code-split: admin bundle only downloads when /admin is visited.
const AdminLoginPage  = lazy(() => import("@/admin/auth/LoginPage"));
const RequireAdmin    = lazy(() => import("@/admin/auth/RequireAdmin"));
const AdminLayout     = lazy(() => import("@/admin/layout/AdminLayout"));
const AdminDashboard  = lazy(() => import("@/admin/pages/Dashboard"));
const AdminSubmissions = lazy(() => import("@/admin/pages/ContactSubmissions"));
const AdminListingsList = lazy(() => import("@/admin/pages/ListingsList"));
const AdminListingForm = lazy(() => import("@/admin/pages/ListingForm"));
const AdminCaseStudiesList = lazy(() => import("@/admin/pages/CaseStudiesList"));
const AdminCaseStudyForm = lazy(() => import("@/admin/pages/CaseStudyForm"));
const AdminTestimonialsList = lazy(() => import("@/admin/pages/TestimonialsList"));
const AdminTestimonialForm = lazy(() => import("@/admin/pages/TestimonialForm"));
const AdminAwardsList = lazy(() => import("@/admin/pages/AwardsList"));
const AdminAwardForm = lazy(() => import("@/admin/pages/AwardForm"));
const AdminBlogList = lazy(() => import("@/admin/pages/BlogList"));
const AdminBlogForm = lazy(() => import("@/admin/pages/BlogForm"));
const AdminBlogCategories = lazy(() => import("@/admin/pages/BlogCategories"));
const AdminSeoSettings = lazy(() => import("@/admin/pages/SeoSettings"));
const AdminSiteSettings = lazy(() => import("@/admin/pages/SiteSettings"));

function AdminLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#fafafa]">
      <div className="text-sm text-slate-400">Loading admin…</div>
    </div>
  );
}

function PublicSite() {
  return (
    <Layout>
      <SiteTracking />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/framework" element={<Framework />} />
        <Route path="/listings" element={<Listings />} />
        <Route path="/listings/:id" element={<ListingDetail />} />
        <Route path="/results" element={<Results />} />
        <Route path="/results/:id" element={<CaseStudyDetail />} />
        <Route path="/testimonials" element={<Testimonials />} />
        <Route path="/awards" element={<Awards />} />
        <Route path="/calculators" element={<Calculators />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="*" element={<Home />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<AdminLoading />}>
            <Routes>
              {/* Admin — bypasses the public Layout (no navbar/footer/WhatsApp btn) */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route
                path="/admin"
                element={
                  <RequireAdmin>
                    <AdminLayout />
                  </RequireAdmin>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="listings"         element={<AdminListingsList />} />
                <Route path="listings/new"     element={<AdminListingForm />} />
                <Route path="listings/:id"     element={<AdminListingForm />} />
                <Route path="case-studies"     element={<AdminCaseStudiesList />} />
                <Route path="case-studies/new" element={<AdminCaseStudyForm />} />
                <Route path="case-studies/:id" element={<AdminCaseStudyForm />} />
                <Route path="testimonials"     element={<AdminTestimonialsList />} />
                <Route path="testimonials/new" element={<AdminTestimonialForm />} />
                <Route path="testimonials/:id" element={<AdminTestimonialForm />} />
                <Route path="awards"           element={<AdminAwardsList />} />
                <Route path="awards/new"       element={<AdminAwardForm />} />
                <Route path="awards/:id"       element={<AdminAwardForm />} />
                <Route path="blog"             element={<AdminBlogList />} />
                <Route path="blog/new"         element={<AdminBlogForm />} />
                <Route path="blog/categories"  element={<AdminBlogCategories />} />
                <Route path="blog/:id"         element={<AdminBlogForm />} />
                <Route path="submissions"      element={<AdminSubmissions />} />
                <Route path="seo"              element={<AdminSeoSettings />} />
                <Route path="settings"         element={<AdminSiteSettings />} />
              </Route>

              {/* Public site — everything else */}
              <Route path="/*" element={<PublicSite />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
